import { useEffect, type RefObject } from 'react';

/**
 * Moves focus to the search bar on Ctrl/Cmd-K or `/`.
 *
 * `/` is a plain character wherever text is being typed, so it only fires when focus is
 * outside a field. Ctrl-K works from a field too — it is the shortcut people reach for
 * mid-edit — but neither one steals focus out of an open modal.
 */
export function useSearchShortcut(input: RefObject<HTMLInputElement | null>): void {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const typing = isTypingTarget(event.target);
      const slash = event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey && !typing;
      const commandK =
        (event.key === 'k' || event.key === 'K') &&
        (event.ctrlKey || event.metaKey) &&
        !event.altKey;
      if (!slash && !commandK) return;
      if (document.querySelector('[role="dialog"]') != null) return;

      const field = input.current;
      if (field == null) return;
      event.preventDefault();
      field.focus();
      // Selecting the current term means the next keystroke starts a fresh search rather
      // than appending to whatever was already there.
      field.select();
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [input]);
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}
